
import React, { useState, useEffect } from 'react';
import { COLORS } from '../constants';

const CASE_STUDY_DATA = [
  {
    id: '01',
    client: 'Medtronic',
    quote: '"We have used services from Xurya for most of our stations, this is our strategic step to continue to increase the number of solar panel usage for our stations, we are very satisfied with the services from Xurya."',
    author: 'Jeniffer Kolobaly',
    role: 'CTO EV Medtronic',
    label: 'Electric car charging station',
    icon: '⚡',
    image: 'https://i.imgur.com/ALkF5g0.jpeg?q=60&w=1200'
  },
  {
    id: '02',
    client: 'Telia Cygate',
    quote: '"Housing in the future really needs clean and reliable energy sources, Xurya really helps us to continue developing our renewable energy solutions for residential communities."',
    author: 'Manahan Mana',
    role: 'CTO Hjung Motor',
    label: 'Residential solar panels',
    icon: '🏠',
    image: 'https://i.imgur.com/ujIljAH.jpeg?q=60&w=1200'
  },
  {
    id: '03',
    client: 'Eco Industrial',
    quote: '"Our manufacturing plants now run on 60% wind energy thanks to the industrial infrastructure provided. The efficiency gains were immediate and significant for our bottom line."',
    author: 'Robert Chen',
    role: 'Director of Operations',
    label: 'Wind power for industry',
    icon: '🌬️',
    image: 'https://i.imgur.com/9MCD40r.jpeg?q=60&w=1200'
  }
];

const SLIDE_DURATION = 5000;

const CaseStudies: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          setActiveTab((prevTab) => (prevTab + 1) % CASE_STUDY_DATA.length);
          return 0;
        }
        return prev + (100 / (SLIDE_DURATION / 100));
      });
    }, 100);

    return () => clearInterval(timer);
  }, [activeTab]);

  const handleTabClick = (index: number) => {
    setActiveTab(index);
    setProgress(0);
  };

  const active = CASE_STUDY_DATA[activeTab];

  return (
    <section className="py-12 md:py-16 bg-white overflow-hidden" id="case-studies">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-20">
        
        <div className="text-center mb-8 md:mb-10 reveal-item">
          <h2 className="fluid-heading-lg font-bold text-[#1E1E1E] leading-tight">
            See how we solve <br />
            <span className="text-gray-400 font-medium">problems, </span>right on target
          </h2>
        </div>

        <div className="bg-[#F8FAFA] rounded-[0.8rem] p-6 md:p-12 lg:p-14 shadow-sm border border-gray-100 grid lg:grid-cols-[1fr_0.85fr] gap-8 lg:gap-16 items-center min-h-[450px] md:min-h-[550px]">
          
          <div className="flex flex-col h-full justify-between">
            <div className="space-y-6 md:space-y-10 reveal-item" style={{ transitionDelay: '0.1s' }}>
              <h3 className="text-xl md:text-2xl font-bold text-[#1E1E1E]">{active.client}</h3>
              
              <p className="text-lg md:text-2xl font-medium leading-[1.6] text-[#1E1E1E] max-w-xl">
                {active.quote}
              </p>

              <button 
                className="bg-[#00D47E] text-white text-[13px] font-bold px-8 py-3.5 rounded-full hover:scale-105 active:scale-95 transition-all shadow-lg shadow-emerald-500/20 w-fit"
              >
                View case study
              </button>
            </div>

            <div className="mt-8 md:mt-16 reveal-item" style={{ transitionDelay: '0.2s' }}>
              <p className="text-sm md:text-base font-bold text-[#1E1E1E]">
                {active.author} <span className="text-gray-400 font-medium ml-2">— {active.role}</span>
              </p>
            </div>
          </div>

          <div className="relative w-full aspect-[4/3] rounded-[0.8rem] overflow-hidden shadow-2xl bg-gray-200 reveal-item" style={{ transitionDelay: '0.3s' }}>
            <img 
              key={active.id}
              src={active.image} 
              alt={active.label} 
              className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out"
              loading="lazy"
            />
          </div>
        </div>

        <div className="mt-8 md:mt-12 max-w-4xl mx-auto reveal-item" style={{ transitionDelay: '0.4s' }}>
          <div className="grid grid-cols-3 gap-3 md:gap-10">
            {CASE_STUDY_DATA.map((item, index) => (
              <div key={item.id} className="group cursor-pointer" onClick={() => handleTabClick(index)}>
                <div className="h-[2px] w-full bg-gray-100 rounded-full mb-3 md:mb-5 overflow-hidden">
                  <div 
                    className="h-full bg-emerald-500 transition-all ease-linear"
                    style={{ 
                      width: activeTab === index ? `${progress}%` : activeTab > index ? '100%' : '0%' 
                    }}
                  />
                </div>
                
                <div className="flex flex-col md:flex-row items-center justify-center gap-1 md:gap-3 transition-opacity duration-300">
                  <span className={`text-sm md:text-base grayscale group-hover:grayscale-0 ${activeTab === index ? 'grayscale-0' : ''}`}>
                    {item.icon}
                  </span>
                  <p className={`text-[8px] md:text-[12px] text-center md:text-left font-bold uppercase tracking-widest transition-colors ${activeTab === index ? 'text-[#1E1E1E]' : 'text-gray-400 group-hover:text-gray-600'}`}>
                    {item.label}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>
    </section>
  );
};

export default CaseStudies;
